import React, {useEffect, useState} from 'react'
import axios from 'axios'
import {Link} from 'react-router-dom'

const apiUrl = process.env.REACT_APP_API_URL

interface CourseSurveyResult {
  semesterCourseId: number
  courseCode?: string
  courseName?: string
  semesterName?: string
  organizationName?: string
  responseCount: number
  surveyEndAt?: string
  hasFeedback: boolean
}

interface ConsultantSurveyResult {
  surveyId: number
  surveyGuid: string
  surveyName?: string
  surveyTypeName?: string
  semesterName?: string
  responseCount: number
  surveyEndAt?: string
}

interface SurveyFeedbackOverview {
  courses: CourseSurveyResult[]
  consultantSurveys: ConsultantSurveyResult[]
}

const formatDate = (value?: string) => {
  if (!value) return null

  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('tr-TR')
}

const SurveyFeedbackDashboardBox: React.FC = () => {
  const [overview, setOverview] = useState<SurveyFeedbackOverview>({
    courses: [],
    consultantSurveys: [],
  })
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    let isMounted = true

    axios
      .get(`${apiUrl}/Custom/GetMySurveyFeedbackOverview`)
      .then((response) => {
        if (!isMounted) return

        setOverview({
          courses: Array.isArray(response.data?.courses) ? response.data.courses : [],
          consultantSurveys: Array.isArray(response.data?.consultantSurveys)
            ? response.data.consultantSurveys
            : [],
        })
      })
      .catch((error) => {
        if (!isMounted) return

        console.error('Anket sonuçları alınamadı:', error)
        setHasError(true)
      })
      .finally(() => {
        if (isMounted) setIsLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [])

  if (isLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center py-10'>
        <span className='spinner-border spinner-border-sm text-primary me-3' role='status' />
        <span className='text-muted'>Anket sonuçları yükleniyor...</span>
      </div>
    )
  }

  if (hasError) {
    return (
      <div className='alert alert-danger mb-0' role='alert'>
        Anket sonuçları şu anda alınamıyor. Lütfen daha sonra tekrar deneyiniz.
      </div>
    )
  }

  const hasResults = overview.courses.length > 0 || overview.consultantSurveys.length > 0

  if (!hasResults) {
    return (
      <div className='border border-dashed rounded p-8 text-center'>
        <i className='bi bi-bar-chart fs-2 text-muted d-block mb-3' aria-hidden='true' />
        <div className='fw-semibold'>Görüntülenebilecek anket sonucu bulunmuyor.</div>
      </div>
    )
  }

  return (
    <div className='row g-5'>
      <div className='col-12 col-xl-7'>
        <section className='border rounded h-100'>
          <header className='d-flex align-items-center justify-content-between border-bottom px-6 py-5'>
            <div>
              <h2 className='fs-5 fw-bold mb-1'>Ders anket sonuçları</h2>
              <div className='text-muted fs-7'>Sonuçlanan ders değerlendirme anketleri</div>
            </div>
            <span className='badge badge-light-primary'>{overview.courses.length} ders</span>
          </header>

          <div className='list-group list-group-flush'>
            {overview.courses.map((course) => {
              const endDate = formatDate(course.surveyEndAt)

              return (
                <Link
                  key={course.semesterCourseId}
                  to='/surveyresult'
                  state={course.semesterCourseId}
                  className='list-group-item list-group-item-action px-6 py-5'
                >
                  <div className='d-flex align-items-start justify-content-between gap-4'>
                    <div className='min-w-0'>
                      <div className='fw-bold text-gray-900 text-truncate'>
                        {course.courseCode ? `${course.courseCode} - ` : ''}
                        {course.courseName || 'Ders'}
                      </div>
                      <div className='text-muted fs-7 mt-1'>
                        {[course.semesterName, course.organizationName].filter(Boolean).join(' · ')}
                      </div>
                      <div className='d-flex flex-wrap gap-3 mt-3'>
                        <span className='badge badge-light'>
                          {course.responseCount} katılımcı
                        </span>
                        {endDate && <span className='badge badge-light'>{endDate}</span>}
                        <span
                          className={`badge ${
                            course.hasFeedback ? 'badge-light-success' : 'badge-light-warning'
                          }`}
                        >
                          {course.hasFeedback ? 'Geri bildirim tamamlandı' : 'Geri bildirim bekliyor'}
                        </span>
                      </div>
                    </div>
                    <i className='bi bi-chevron-right text-muted fs-4' aria-hidden='true' />
                  </div>
                </Link>
              )
            })}
          </div>
        </section>
      </div>

      <div className='col-12 col-xl-5'>
        <section className='border rounded h-100'>
          <header className='d-flex align-items-center justify-content-between border-bottom px-6 py-5'>
            <div>
              <h2 className='fs-5 fw-bold mb-1'>Danışman değerlendirmeleri</h2>
              <div className='text-muted fs-7'>Danışmanı olduğunuz öğrencilerin yanıtları</div>
            </div>
            <span className='badge badge-light-info'>
              {overview.consultantSurveys.length} anket
            </span>
          </header>

          {overview.consultantSurveys.length === 0 ? (
            <div className='px-6 py-10 text-center text-muted'>
              <i className='bi bi-people fs-2 d-block mb-3' aria-hidden='true' />
              Danışman değerlendirme sonucu bulunmuyor.
            </div>
          ) : (
            <div className='list-group list-group-flush'>
              {overview.consultantSurveys.map((survey) => {
                const endDate = formatDate(survey.surveyEndAt)

                return (
                  <Link
                    key={survey.surveyId}
                    to='/surveyresultgeneral'
                    state={{
                      surveyId: survey.surveyId,
                      guid: survey.surveyGuid,
                      scope: 'consultant',
                    }}
                    className='list-group-item list-group-item-action px-6 py-5'
                  >
                    <div className='d-flex align-items-start justify-content-between gap-4'>
                      <div className='min-w-0'>
                        <div className='fw-bold text-gray-900'>
                          {survey.surveyTypeName || survey.surveyName || 'Danışman değerlendirme anketi'}
                        </div>
                        <div className='text-muted fs-7 mt-1'>
                          {[survey.semesterName, endDate].filter(Boolean).join(' · ')}
                        </div>
                        <span className='badge badge-light-info mt-3'>
                          {survey.responseCount} katılımcı
                        </span>
                      </div>
                      <i className='bi bi-chevron-right text-muted fs-4' aria-hidden='true' />
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

export default SurveyFeedbackDashboardBox
